// ─────────────────────────────────────────────────────────────────────────────

/**
 * Marks every local `<img>` (relative, root-relative, or same-origin URL) with
 * `data-ot-ignore` so OneTrust's auto-block scanner skips it. Existing images
 * are marked synchronously; future ones and delayed `src` assignments are caught
 * by a `MutationObserver` watching `document.body`. Idempotent — repeat calls
 * are no-ops.
 *
 * Must be called BEFORE OneTrust's scripts are appended so this observer
 * wins the race against OneTrust's own scanner. Third-party images are left
 * alone so consent gating still applies to them.
 */
export function addLocalImageSkip() {
  if (state.hasAdded) return;

  state.hasAdded = true;

  document.querySelectorAll('img').forEach(markIfLocal);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach(processMutation);
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['src'],
    childList: true,
    subtree: true,
  });
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function processMutation(mutation: MutationRecord) {
  if (mutation.type === 'attributes') {
    processAttributeMutation(mutation);

    return;
  }

  mutation.addedNodes.forEach(processAddedNode);
}

function processAttributeMutation(mutation: MutationRecord) {
  const { target } = mutation;

  if (target instanceof HTMLImageElement) {
    markIfLocal(target);
  }
}

function processAddedNode(node: Node) {
  if (node instanceof HTMLImageElement) {
    markIfLocal(node);

    return;
  }

  if (node instanceof Element) {
    node.querySelectorAll('img').forEach(markIfLocal);
  }
}

function markIfLocal(img: HTMLImageElement) {
  if (!checkIfLocalImage(img)) return;

  img.setAttribute('data-ot-ignore', 'true');
}

function checkIfLocalImage(img: HTMLImageElement) {
  const src = img.getAttribute('src');

  if (!src) return false;

  try {
    const url = new URL(src, window.location.href);

    return url.origin === window.location.origin;
  } catch {
    return false;
  }
}

// ─── State ───────────────────────────────────────────────────────────────────

const state = { hasAdded: false };
